import { create } from 'zustand';
import api from '@/lib/api';
import type { User, AuthResult, ApiResponse } from '@/types/auth';

interface AuthState {
 user: User | null;
 isAuthenticated: boolean;
 isLoading: boolean;
 error: string | null;

 // Actions
 login: (email: string, password: string) => Promise<void>;
 register: (
  email: string,
  password: string,
  fullName?: string
 ) => Promise<{ needsVerification: boolean }>;
 logout: () => Promise<void>;
 fetchUser: () => Promise<void>;
 verifyEmail: (email: string, code: string) => Promise<void>;
 resendVerificationCode: (email: string) => Promise<void>;
 forgotPassword: (email: string) => Promise<void>;
 resetPassword: (
  email: string,
  code: string,
  newPassword: string
 ) => Promise<void>;
 refreshProfile: () => Promise<void>;
 setUser: (user: User | null) => void;
 getGoogleAuthUrl: () => Promise<{ authUrl: string; state: string }>;
 getGitHubAuthUrl: () => Promise<{ authUrl: string; state: string }>;
 clearAuth: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
 user: null,
 isAuthenticated: false,
 isLoading: true,
 error: null,

 login: async (email, password) => {
  set({ isLoading: true, error: null });
  try {
   const response = await api.post<ApiResponse<AuthResult>>('/auth/login', {
    email,
    password,
   });
   if (response.data.success && response.data.data) {
    const { user, token, refreshToken } = response.data.data;
    localStorage.setItem('auth_token', token);
    localStorage.setItem('refresh_token', refreshToken);
    localStorage.setItem('user_data', JSON.stringify(user));
    set({ user, isAuthenticated: true, isLoading: false });
   }
  } catch (error: any) {
   const message = error.response?.data?.error?.message || 'Login failed';
   set({ error: message, isLoading: false });
   throw new Error(message);
  }
 },

 register: async (email, password, fullName) => {
  set({ isLoading: true, error: null });
  try {
   const response = await api.post<
    ApiResponse<{ user: User; needsVerification: boolean; message: string }>
   >('/auth/register', {
    email,
    password,
    fullName,
   });
   set({ isLoading: false });
   return { needsVerification: response.data.data.needsVerification };
  } catch (error: any) {
   const message =
    error.response?.data?.error?.message || 'Registration failed';
   set({ error: message, isLoading: false });
   throw new Error(message);
  }
 },

 logout: async () => {
  set({ isLoading: true });
  try {
   await api.post('/auth/logout');
  } finally {
   localStorage.removeItem('auth_token');
   localStorage.removeItem('refresh_token');
   localStorage.removeItem('user_data');
   set({ user: null, isAuthenticated: false, isLoading: false });
  }
 },

 fetchUser: async () => {
  const token = localStorage.getItem('auth_token');
  const userData = localStorage.getItem('user_data');

  if (token && userData) {
   try {
    const parsedUser = JSON.parse(userData);
    set({ user: parsedUser, isAuthenticated: true });

    // Background refresh to ensure fresh data
    get().refreshProfile();
   } catch (error) {
    console.error('Failed to parse user data:', error);
   }
  }
  set({ isLoading: false });
 },

 refreshProfile: async () => {
  try {
   const response = await api.get<ApiResponse<{ user: User }>>('/auth/me');
   if (response.data.success && response.data.data) {
    const updatedUser = response.data.data.user;
    localStorage.setItem('user_data', JSON.stringify(updatedUser));
    set({
     user: updatedUser,
     isAuthenticated: true,
     // Explicitly ensure store state is synced
     isLoading: false,
    });
   }
  } catch (error) {
   console.error('Failed to refresh profile:', error);
  }
 },

 verifyEmail: async (email, code) => {
  set({ isLoading: true, error: null });
  try {
   const response = await api.post<
    ApiResponse<AuthResult & { message: string }>
   >('/auth/verify-email', { email, code });
   if (response.data.success && response.data.data) {
    const { user, token, refreshToken } = response.data.data;
    localStorage.setItem('auth_token', token);
    localStorage.setItem('refresh_token', refreshToken);
    localStorage.setItem('user_data', JSON.stringify(user));
    set({ user, isAuthenticated: true, isLoading: false });
   }
  } catch (error: any) {
   const message =
    error.response?.data?.error?.message || 'Verification failed';
   set({ error: message, isLoading: false });
   throw new Error(message);
  }
 },

 resendVerificationCode: async (email) => {
  try {
   await api.post('/auth/resend-code', { email });
  } catch (error: any) {
   throw new Error(
    error.response?.data?.error?.message || 'Failed to resend code'
   );
  }
 },

 forgotPassword: async (email) => {
  try {
   await api.post('/auth/forgot-password', { email });
  } catch (error: any) {
   throw new Error(
    error.response?.data?.error?.message || 'Failed to send reset code'
   );
  }
 },

 resetPassword: async (email, code, newPassword) => {
  set({ isLoading: true, error: null });
  try {
   const response = await api.post<
    ApiResponse<AuthResult & { message: string }>
   >('/auth/reset-password', {
    email,
    code,
    newPassword,
   });
   if (response.data.success && response.data.data) {
    const { user, token, refreshToken } = response.data.data;
    localStorage.setItem('auth_token', token);
    localStorage.setItem('refresh_token', refreshToken);
    localStorage.setItem('user_data', JSON.stringify(user));
    set({ user, isAuthenticated: true, isLoading: false });
   }
  } catch (error: any) {
   const message =
    error.response?.data?.error?.message || 'Password reset failed';
   set({ error: message, isLoading: false });
   throw new Error(message);
  }
 },

 getGoogleAuthUrl: async () => {
  const response =
   await api.get<ApiResponse<{ authUrl: string; state: string }>>(
    '/auth/oauth/google'
   );
  return response.data.data;
 },

 getGitHubAuthUrl: async () => {
  const response =
   await api.get<ApiResponse<{ authUrl: string; state: string }>>(
    '/auth/oauth/github'
   );
  return response.data.data;
 },

 setUser: (user) => set({ user, isAuthenticated: !!user }),
 clearAuth: () => {
  localStorage.removeItem('auth_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user_data');
  set({ user: null, isAuthenticated: false, error: null });
 },
}));
